import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { listSaldosEcheq, type SaldoEcheq } from '@/shared/api/controllers/echeqs';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { formatMoney } from '@/shared/lib/money';
import { cn } from '@/shared/lib/cn';

/** Estados en los que la venta ya pasó a despacho con E-cheqs sin cobrar. */
const EN_DESPACHO = new Set(['Pendiente Despacho', 'Despachado', 'Pendiente Puesta Marcha']);

/**
 * Ventas con E-cheqs sin cobrar del todo, por id. Una sola query para toda la
 * app (lista chica). Cuelga de ['ventas']: confirmar / anular / registrar
 * cobros, las acciones de E-cheqs y la puesta en marcha ya la refrescan.
 */
export function useSaldosEcheq(): Map<string, SaldoEcheq> {
  const { config } = useActiveCountry();
  const q = useQuery({
    queryKey: ['ventas', 'echeq-saldos', config.code],
    queryFn: listSaldosEcheq,
    enabled: config.code === 'AR',
    staleTime: 60_000,
  });
  return useMemo(() => new Map((q.data ?? []).map((s) => [s.ventaId, s])), [q.data]);
}

/** Saldo de la venta sólo si ya está en despacho (antes, el estado ya dice "Pago Parcial"). */
export function saldoEnDespacho(saldos: Map<string, SaldoEcheq>, ventaId: string, status: string | null | undefined) {
  const s = saldos.get(ventaId);
  return s && EN_DESPACHO.has(status ?? '') ? s : undefined;
}

/** `sinMontos`: pantallas de Mantenimiento (no ven montos de las ventas, sólo %). */
export function echeqSaldoDetalle(s: SaldoEcheq, sinMontos = false): string {
  const ars = (n: number) => formatMoney(n, s.moneda === 'USD' ? 'USD' : 'ARS');
  const montos = !sinMontos && s.total != null && s.cobrado != null ? { total: s.total, cobrado: s.cobrado } : null;
  const cobrado = montos
    ? `Cobrado ${ars(montos.cobrado)} de ${ars(montos.total)} (${s.pctCobrado}%).`
    : `Cobrado el ${s.pctCobrado}% de la venta.`;
  if (s.retenido) return `${cobrado} Se puede despachar desde el ${s.pctRequerido}%: faltan acreditar E-cheqs.`;
  const falta = montos ? ` Faltan cobrar ${ars(montos.total - montos.cobrado)}.` : '';
  return `${cobrado}${falta} Se puede despachar y hacer la puesta en marcha; la venta se cierra cuando esté paga.`;
}

/** Tag "Retenido" (no se puede despachar) o "Pago parcial" (se despacha, no se cierra). */
export function EcheqSaldoTag({ saldo, className }: { saldo: SaldoEcheq | undefined; className?: string }) {
  if (!saldo) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex cursor-help items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide',
            saldo.retenido ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700',
            className,
          )}
        >
          <Landmark className="h-3 w-3" aria-hidden />
          {saldo.retenido ? `Retenido · ${saldo.pctCobrado}%` : `Pago parcial · ${saldo.pctCobrado}%`}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="font-medium">{saldo.retenido ? 'Despacho retenido' : 'Pago parcial con E-cheqs'}</p>
        <p className="mt-0.5 text-xs leading-snug">{echeqSaldoDetalle(saldo)}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Para las listas: el estado real de la venta (sin estados inventados) y al
 * lado un ícono — rojo si el despacho está retenido, naranja si es pago
 * parcial — con el detalle en el tooltip.
 */
export function EstadoConSaldoEcheq({
  saldo,
  sinMontos = false,
  children,
}: {
  saldo: SaldoEcheq | undefined;
  sinMontos?: boolean;
  children: React.ReactNode;
}) {
  if (!saldo) return <>{children}</>;
  const titulo = saldo.retenido
    ? `Despacho retenido · ${saldo.pctCobrado}% de ${saldo.pctRequerido}%`
    : `Pago parcial con E-cheqs · ${saldo.pctCobrado}%`;
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex h-6 w-6 shrink-0 cursor-help items-center justify-center rounded-md',
              saldo.retenido ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700',
            )}
            aria-label={titulo}
          >
            <Landmark className="h-3.5 w-3.5" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">{titulo}</p>
          <p className="mt-0.5 text-xs leading-snug">{echeqSaldoDetalle(saldo, sinMontos)}</p>
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
